import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Button, CircularProgress, Alert,
  IconButton, Chip, Tooltip, Tab, Tabs, Collapse, Divider, LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Psychology as PsychologyIcon, Assessment as AssessmentIcon, TrendingUp as TrendingIcon,
  Warning as WarningIcon, Lightbulb as LightbulbIcon, Error as ErrorIcon,
  CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, People as PeopleIcon,
  LocalFireDepartment as HotIcon, CalendarMonth as CalendarIcon,
  Route as RouteIcon, Phone as PhoneIcon, Person as PersonIcon,
  Flag as FlagIcon, ArrowForward as ArrowIcon, Article as ArticleIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

function formatDate(date: string | Date): string {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatShortDate(date: string | Date): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function getScoreColor(score: number): 'error' | 'warning' | 'info' | 'success' {
  if (score >= 80) return 'success';
  if (score >= 60) return 'info';
  if (score >= 40) return 'warning';
  return 'error';
}

function getSeverityColor(severity: string): 'error' | 'warning' | 'info' | 'default' {
  switch (severity?.toLowerCase()) {
    case 'high': case 'alta': return 'error';
    case 'medium': case 'media': return 'warning';
    case 'low': case 'baja': return 'info';
    default: return 'default';
  }
}

function getInterestColor(interest: string): 'success' | 'warning' | 'error' | 'default' {
  switch (interest?.toLowerCase()) {
    case 'alto': return 'success';
    case 'medio': return 'warning';
    case 'bajo': return 'error';
    case 'ninguno': return 'default';
    default: return 'default';
  }
}

const ROUTE_COLORS: string[] = [
  '#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#c62828',
  '#00838f', '#4e342e', '#283593', '#558b2f', '#ad1457',
];

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const BotInsightsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [insights, setInsights] = useState<any[]>([]);
  const [leadReports, setLeadReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [expandedReportText, setExpandedReportText] = useState<string | null>(null);
  const [resolvedSuggestions, setResolvedSuggestions] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('resolvedSuggestions') || '[]')); }
    catch { return new Set(); }
  });

  const toggleResolved = (key: string) => {
    setResolvedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem('resolvedSuggestions', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const fetchInsights = useCallback(async () => {
    try {
      const res = await apiService.getBotInsights();
      const data = res.data || [];
      // Sort most recent first
      data.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setInsights(data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Error al cargar insights');
    }
  }, []);

  const fetchLeadReports = useCallback(async () => {
    try {
      const res = await apiService.getBotLeadReports();
      const data = res.data || [];
      data.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setLeadReports(data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Error al cargar reportes');
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchInsights(), fetchLeadReports()]);
    setLoading(false);
  }, [fetchInsights, fetchLeadReports]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleInsight = useCallback((id: string) => {
    setExpandedInsight((prev) => prev === id ? null : id);
  }, []);

  const toggleReport = useCallback((id: string) => {
    setExpandedReport((prev) => prev === id ? null : id);
  }, []);

  const toggleReportText = useCallback((id: string) => {
    setExpandedReportText((prev) => prev === id ? null : id);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Inteligencia del Bot</Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={fetchAll} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Analisis de Calidad"
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Reportes de Leads"
          />
        </Tabs>
      </Paper>

      {loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      )}

      {/* Tab 1: Analisis de Calidad */}
      {!loading && (
        <TabPanel value={tabValue} index={0}>
          {insights.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No hay analisis de calidad disponibles</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {insights.map((insight, idx) => {
                const analysis = insight.analysis || {};
                const qualityScore = analysis.qualityScore ?? 0;
                const problems = analysis.problems || [];
                const patterns = analysis.patterns || [];
                const suggestions = insight.suggestions || [];
                const id = insight._id || `insight-${idx}`;
                const isExpanded = expandedInsight === id;

                return (
                  <Card key={id} variant="outlined">
                    <CardContent
                      sx={{ cursor: 'pointer', '&:last-child': { pb: 2 } }}
                      onClick={() => toggleInsight(id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          {/* Quality Score Circle */}
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                              variant="determinate"
                              value={qualityScore}
                              size={56}
                              thickness={5}
                              color={getScoreColor(qualityScore)}
                            />
                            <Box
                              sx={{
                                position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              <Typography variant="body2" fontWeight={700} color="text.primary">
                                {qualityScore}
                              </Typography>
                            </Box>
                          </Box>

                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Analisis de Calidad
                              </Typography>
                              {insight.type && (
                                <Chip label={insight.type} size="small" variant="outlined" />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(insight.createdAt)}
                              </Typography>
                              {problems.length > 0 && (
                                <Chip
                                  icon={<WarningIcon />}
                                  label={`${problems.length} problema${problems.length !== 1 ? 's' : ''}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {suggestions.length > 0 && (
                                <Chip
                                  icon={<LightbulbIcon />}
                                  label={`${suggestions.length} sugerencia${suggestions.length !== 1 ? 's' : ''}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </CardContent>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 2 }}>
                        {/* Problems Section */}
                        {problems.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                              <ErrorIcon fontSize="small" color="error" />
                              Problemas Detectados
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {problems.map((problem: any, pi: number) => (
                                <Paper key={pi} variant="outlined" sx={{ p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.04)' }}>
                                  <Typography variant="body2">{typeof problem === 'string' ? problem : problem.description || problem.message || JSON.stringify(problem)}</Typography>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Patterns Section */}
                        {patterns.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                              <TrendingIcon fontSize="small" color="info" />
                              Patrones Identificados
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {patterns.map((pattern: any, pi: number) => (
                                <Chip
                                  key={pi}
                                  label={typeof pattern === 'string' ? pattern : pattern.name || pattern.description || JSON.stringify(pattern)}
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Suggestions Section */}
                        {suggestions.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                              <LightbulbIcon fontSize="small" color="warning" />
                              Sugerencias de Mejora
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {suggestions.map((suggestion: any, si: number) => {
                                const sev = suggestion.severity || suggestion.priority || '';
                                const suggKey = `${id}-${si}`;
                                const isResolved = resolvedSuggestions.has(suggKey);
                                return (
                                  <Paper key={si} variant="outlined" sx={{ p: 2, opacity: isResolved ? 0.5 : 1, borderColor: isResolved ? 'success.main' : undefined }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {sev && (
                                          <Chip
                                            label={sev}
                                            size="small"
                                            color={getSeverityColor(sev)}
                                          />
                                        )}
                                        <Typography variant="body2" fontWeight={600} sx={{ textDecoration: isResolved ? 'line-through' : 'none' }}>
                                          {suggestion.title || suggestion.suggestion || (typeof suggestion === 'string' ? suggestion : '')}
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        variant={isResolved ? 'contained' : 'outlined'}
                                        color={isResolved ? 'success' : 'inherit'}
                                        startIcon={isResolved ? <CheckCircleIcon /> : undefined}
                                        onClick={() => toggleResolved(suggKey)}
                                        sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
                                      >
                                        {isResolved ? 'Resuelta' : 'Marcar resuelta'}
                                      </Button>
                                    </Box>
                                    {suggestion.description && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {suggestion.description}
                                      </Typography>
                                    )}
                                    {(suggestion.currentResponse || suggestion.correctResponse) && (
                                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        {suggestion.currentResponse && (
                                          <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.04)' }}>
                                              <Typography variant="caption" color="error" fontWeight={600}>Respuesta actual:</Typography>
                                              <Typography variant="body2" sx={{ mt: 0.5 }}>{suggestion.currentResponse}</Typography>
                                            </Paper>
                                          </Grid>
                                        )}
                                        {suggestion.correctResponse && (
                                          <Grid size={{ xs: 12, md: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'rgba(46, 125, 50, 0.04)' }}>
                                              <Typography variant="caption" color="success.main" fontWeight={600}>Respuesta correcta:</Typography>
                                              <Typography variant="body2" sx={{ mt: 0.5 }}>{suggestion.correctResponse}</Typography>
                                            </Paper>
                                          </Grid>
                                        )}
                                      </Grid>
                                    )}
                                  </Paper>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {problems.length === 0 && suggestions.length === 0 && patterns.length === 0 && (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            <Typography color="text.secondary">Sin problemas ni sugerencias</Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Card>
                );
              })}
            </Box>
          )}
        </TabPanel>
      )}

      {/* Tab 2: Reportes de Leads */}
      {!loading && (
        <TabPanel value={tabValue} index={1}>
          {leadReports.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No hay reportes de leads disponibles</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {leadReports.map((report, idx) => {
                const id = report._id || `report-${idx}`;
                const isExpanded = expandedReport === id;
                const isTextExpanded = expandedReportText === id;
                const analyses = report.analyses || [];
                const routeSummary = report.routeSummary || {};
                const routeKeys = Object.keys(routeSummary);

                return (
                  <Card key={id} variant="outlined">
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      {/* Summary Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {formatShortDate(report.date)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Stats Row */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <PeopleIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                            <Typography variant="h5" fontWeight={700}>{report.totalLeads || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">Total Leads</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <HotIcon sx={{ color: 'error.main', mb: 0.5 }} />
                            <Typography variant="h5" fontWeight={700} color="error.main">{report.calientes || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">Calientes</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <CalendarIcon sx={{ color: 'success.main', mb: 0.5 }} />
                            <Typography variant="h5" fontWeight={700} color="success.main">{report.citas || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">Citas</Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                            <RouteIcon sx={{ color: 'info.main', mb: 0.5 }} />
                            <Typography variant="h5" fontWeight={700}>{routeKeys.length}</Typography>
                            <Typography variant="caption" color="text.secondary">Zonas</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Conversion Bar */}
                      {report.totalLeads > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Tasa de leads calientes</Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {Math.round(((report.calientes || 0) / report.totalLeads) * 100)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.round(((report.calientes || 0) / report.totalLeads) * 100)}
                            color="error"
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      )}

                      {/* Route Summary Chips */}
                      {routeKeys.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Distribucion por zona:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {routeKeys.map((zone, zi) => (
                              <Chip
                                key={zone}
                                label={`${zone}: ${routeSummary[zone]}`}
                                size="small"
                                sx={{
                                  backgroundColor: ROUTE_COLORS[zi % ROUTE_COLORS.length],
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Expandable: Lead Analyses */}
                      {analyses.length > 0 && (
                        <>
                          <Button
                            size="small"
                            onClick={() => toggleReport(id)}
                            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mb: 1 }}
                          >
                            {isExpanded ? 'Ocultar' : 'Ver'} {analyses.length} lead{analyses.length !== 1 ? 's' : ''} analizados
                          </Button>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                              {analyses.map((lead: any, li: number) => (
                                <Paper key={li} variant="outlined" sx={{ p: 2 }}>
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon fontSize="small" color="action" />
                                        <Box>
                                          <Typography variant="body2" fontWeight={600}>
                                            {lead.name || 'Sin nombre'}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                              {lead.phone || '-'}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 2 }}>
                                      <Chip
                                        label={lead.interes || 'N/A'}
                                        size="small"
                                        color={getInterestColor(lead.interes)}
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Estado</Typography>
                                        <Typography variant="body2">{lead.estado || '-'}</Typography>
                                      </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FlagIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                        <Typography variant="body2" fontSize="0.85rem">
                                          {lead.accionRequerida || '-'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                  {lead.quiereAgendar && (
                                    <Box sx={{ mt: 1 }}>
                                      <Chip
                                        icon={<CalendarIcon />}
                                        label="Quiere agendar cita"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Paper>
                              ))}
                            </Box>
                          </Collapse>
                        </>
                      )}

                      {/* Full text report */}
                      {report.report && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Button
                            size="small"
                            onClick={() => toggleReportText(id)}
                            startIcon={isTextExpanded ? <ExpandLessIcon /> : <ArticleIcon />}
                          >
                            {isTextExpanded ? 'Ocultar' : 'Ver'} reporte completo
                          </Button>

                          <Collapse in={isTextExpanded} timeout="auto" unmountOnExit>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                mt: 1,
                                maxHeight: 400,
                                overflow: 'auto',
                                backgroundColor: 'grey.50',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: '"Fira Code", "Consolas", monospace',
                                  fontSize: '0.8rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {report.report}
                              </Typography>
                            </Paper>
                          </Collapse>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </TabPanel>
      )}
    </Box>
  );
};

export default BotInsightsPage;
